import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';

// Główny design to „imperial". Stary („classic") jest ukryty i może go odblokować
// administrator w panelu CMS (Ustawienia) — wtedy na stronie pojawia się przełącznik.
export const THEMES = ['classic', 'imperial'];
export const DEFAULT_THEME = 'imperial';
export const CLASSIC_SETTING_KEY = 'settings.classic_theme_enabled'; // wiersz w site_content

const KEY = 'etsukoTheme';
const KEY_CLASSIC = 'etsukoClassicEnabled'; // lokalna kopia ustawienia (bez mignięcia przy starcie)

function readClassicEnabled() {
  try { return localStorage.getItem(KEY_CLASSIC) === 'true'; } catch { return false; }
}

function readChoice() {
  try {
    const v = localStorage.getItem(KEY);
    return THEMES.includes(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

const effective = (choice, classicEnabled) => (classicEnabled ? choice : DEFAULT_THEME);

function apply(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

// Ustaw motyw przed pierwszym renderem, żeby uniknąć „mignięcia".
apply(effective(readChoice(), readClassicEnabled()));

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  isImperial: true,
  classicEnabled: false,
  setTheme: () => {},
  toggle: () => {},
  setClassicEnabled: () => {},
});

export function ThemeProvider({ children }) {
  const [choice, setChoice] = useState(readChoice);
  const [classicEnabled, setClassicEnabledState] = useState(readClassicEnabled);

  // Pobierz ustawienie z bazy (źródło prawdy); lokalna kopia służy tylko do startu.
  useEffect(() => {
    let alive = true;
    if (!supabase) return undefined;
    supabase
      .from('site_content')
      .select('value_pl')
      .eq('key', CLASSIC_SETTING_KEY)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!alive || error) return;
        setClassicEnabledState(data?.value_pl === 'true');
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(KEY_CLASSIC, String(classicEnabled)); } catch { /* prywatny tryb */ }
  }, [classicEnabled]);

  const theme = effective(choice, classicEnabled);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  useEffect(() => {
    try { localStorage.setItem(KEY, choice); } catch { /* prywatny tryb */ }
  }, [choice]);

  const setTheme = useCallback((t) => setChoice(THEMES.includes(t) ? t : DEFAULT_THEME), []);
  const toggle = useCallback(() => setChoice((t) => (t === 'classic' ? 'imperial' : 'classic')), []);
  const setClassicEnabled = useCallback((v) => setClassicEnabledState(Boolean(v)), []);

  const value = useMemo(
    () => ({ theme, isImperial: theme === 'imperial', classicEnabled, setTheme, toggle, setClassicEnabled }),
    [theme, classicEnabled, setTheme, toggle, setClassicEnabled]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
